import React, { useEffect } from 'react';
import { Box, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../store';
import { fetchAcademicYears, setSelectedYear } from '../store/slices/academicYearSlice';

/**
 * Switches the whole app between the previous and current academic year.
 *
 * The current year is the largest one on record; previous is the one before it.
 * Selection lives in the store and is persisted, so every year-aware page follows it.
 */
const AcademicYearToggle: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { current, previous, selected, isLoading } = useSelector((state: RootState) => state.academicYear);

  useEffect(() => {
    dispatch(fetchAcademicYears());
  }, [dispatch]);

  // Nothing meaningful to show until we know what the current year is.
  if (isLoading || current == null) {
    return null;
  }

  const handleChange = (_event: React.MouseEvent<HTMLElement>, value: number | null) => {
    // ToggleButtonGroup emits null when the active button is clicked again; keep the selection.
    if (value == null) return;
    dispatch(setSelectedYear(value));
  };

  const buttonSx = {
    color: 'rgba(255, 255, 255, 0.75)',
    borderColor: 'rgba(255, 255, 255, 0.4)',
    px: 1.5,
    py: 0.25,
    lineHeight: 1.6,
    textTransform: 'none' as const,
    '&.Mui-selected': {
      color: '#fff',
      backgroundColor: 'rgba(255, 255, 255, 0.22)',
      '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.3)' },
    },
    '&.Mui-disabled': {
      color: 'rgba(255, 255, 255, 0.35)',
      borderColor: 'rgba(255, 255, 255, 0.2)',
    },
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)', display: { xs: 'none', sm: 'block' } }}>
        Academic Year
      </Typography>
      <ToggleButtonGroup
        exclusive
        size="small"
        value={selected ?? current}
        onChange={handleChange}
        aria-label="academic year"
      >
        <Tooltip title={previous == null ? 'No earlier academic year on record' : `Show ${previous}`}>
          {/* span keeps the tooltip working while the button is disabled */}
          <span>
            <ToggleButton value={previous ?? -1} disabled={previous == null} sx={buttonSx} aria-label="previous academic year">
              Previous{previous != null ? ` · ${previous}` : ''}
            </ToggleButton>
          </span>
        </Tooltip>
        <ToggleButton value={current} sx={buttonSx} aria-label="current academic year">
          Current · {current}
        </ToggleButton>
      </ToggleButtonGroup>
    </Box>
  );
};

export default AcademicYearToggle;
